import React from 'react';
import Icon from '../../../components/AppIcon';
import departmentalBudgetService from '../../../services/departmentalBudgetService';

const ComplianceImpactCalculator = ({ complianceImpacts }) => {
  if (!complianceImpacts || complianceImpacts?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 text-center">
        <Icon name="Shield" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
        <p className="text-muted-foreground">No compliance cost impact data available</p>
      </div>
    );
  }

  const totalEstimated = complianceImpacts?.reduce((sum, impact) => sum + impact?.estimatedCostKina, 0);
  const totalActual = complianceImpacts?.reduce((sum, impact) => sum + impact?.actualCostKina, 0);
  const totalVariance = totalActual - totalEstimated;

  const getVarianceColor = (variance) => {
    if (variance > 0) return 'text-error';
    if (variance < 0) return 'text-success';
    return 'text-muted-foreground';
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Calculator" size={18} color="var(--color-primary)" />
            <span className="text-sm text-muted-foreground">Total Estimated</span>
          </div>
          <p className="text-2xl font-heading font-bold text-foreground">
            {departmentalBudgetService?.formatKina(totalEstimated)}
          </p>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="DollarSign" size={18} color="var(--color-primary)" />
            <span className="text-sm text-muted-foreground">Total Actual</span>
          </div>
          <p className="text-2xl font-heading font-bold text-foreground">
            {departmentalBudgetService?.formatKina(totalActual)}
          </p>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="TrendingUp" size={18} color={totalVariance > 0 ? 'var(--color-error)' : 'var(--color-success)'} />
            <span className="text-sm text-muted-foreground">Variance</span>
          </div>
          <p className={`text-2xl font-heading font-bold ${getVarianceColor(totalVariance)}`}>
            {totalVariance > 0 ? '+' : ''}{departmentalBudgetService?.formatKina(totalVariance)}
          </p>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Compliance Category</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Regulation</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-foreground">Estimated Cost</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-foreground">Actual Cost</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-foreground">Variance</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-foreground">Affected Employees</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {complianceImpacts?.map((impact) => (
                <tr key={impact?.id} className="hover:bg-muted/50 transition-smooth">
                  <td className="px-4 py-3">
                    <div className="flex items-start gap-2">
                      <Icon name="Shield" size={18} color="var(--color-primary)" className="mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground">{impact?.complianceCategory}</p>
                        {impact?.impactDescription && (
                          <p className="text-xs text-muted-foreground mt-1">{impact?.impactDescription}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {impact?.regulationReference || 'N/A'}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-foreground">
                    {departmentalBudgetService?.formatKina(impact?.estimatedCostKina)}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-foreground">
                    {departmentalBudgetService?.formatKina(impact?.actualCostKina)}
                  </td>
                  <td className={`px-4 py-3 text-right font-medium ${getVarianceColor(impact?.costVarianceKina)}`}>
                    {impact?.costVarianceKina > 0 ? '+' : ''}{departmentalBudgetService?.formatKina(impact?.costVarianceKina)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                      <Icon name="Users" size={12} color="currentColor" />
                      {impact?.affectedEmployees}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ComplianceImpactCalculator;